import React, { useState, useEffect } from "react";

import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Badge,
  Pagination,
  InputGroup,
  Card,
  CardBody,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiCopy } from "react-icons/bi";
import Loader from "../../components/Loader";
import AppConstants from "../../AppConstants";
import './index.scss';

interface ApiTransaction {
  transactionId: string;
  driverUserId: string;
  subscriptionId: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethodId: string;
  paymentType: string;
  cardBrand: string;
  last4Digits: string;
  stripeInvoiceId: string;
  discount: string;
  promocode: string;
  periodStart: string | null;
  periodEnd: string | null;
  maxUsers: number;
  createdAt: string;
  updatedAt: string;
  email: string;
  userName: string;
}

interface PaymentApiResponse {
  success: boolean;
  message: string;
  data: {
    transactions: ApiTransaction[];
    totalTransactions: number;
    totalAmount: number;
    formattedTotalAmount: string;
    successfulTransactions: number;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

interface Payment {
  id: string;
  name: string;
  fullTransactionId: string;
  transId: string;
  date: string;
  email: string;
  maxUsers: number;
  paymentMethod: string;
  promocode: string;
  discount: string;
  amountPaid: string;
  status: "Success" | "Failure" | "Pending";
}

const PaymentsInformation: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  // Updated API endpoint - Make sure this matches your Postman URL
  const ADMIN_GET_ALL_PAYMENT_HISTORY = `${AppConstants.ADMIN_USER_MANAGEMENT_API}/payment-transactions`;

  // Function to get auth token from your app's auth system
  const getAuthToken = () => {
    // Try different storage locations where your app might store the token
    return localStorage.getItem('accessToken') || 
           localStorage.getItem('authToken') || 
           localStorage.getItem('token') || 
           sessionStorage.getItem('accessToken') ||
           sessionStorage.getItem('authToken') || 
           sessionStorage.getItem('token');
  };

  // Function to copy transaction ID to clipboard
  // Debug version to understand production issues
const copyToClipboard = async (text: string) => {
  
  try {
    // Check if clipboard API is available
    if (!navigator.clipboard) {
      throw new Error('Clipboard API not supported');
    }
    
    if (!window.isSecureContext) {
      throw new Error('Secure context required');
    }
    
    await navigator.clipboard.writeText(text);
    toast.success("Transaction ID copied to clipboard!", {
      position: "top-right"
    });
    
  } catch (error) {
    
    // Fallback method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Transaction ID copied to clipboard!", {
          position: "top-right"
        });
      } else {
        throw new Error('execCommand copy failed');
      }
    } catch (fallbackError) {
      toast.error("Copy failed. Please copy manually: " + text, {
        position: "top-right"
      });
    }
  }
};

  // Function to get badge variant based on status
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'Success':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Failure':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Function to transform API transaction data to component format
  const transformPaymentData = (apiTransactions: ApiTransaction[]): Payment[] => {
    return apiTransactions.map((transaction) => {
      // Map API status to display status
      let displayStatus: "Success" | "Failure" | "Pending";
      switch (transaction.status.toLowerCase()) {
        case 'succeeded':
          displayStatus = 'Success';
          break;
        case 'failed':
        case 'canceled':
          displayStatus = 'Failure';
          break;
        case 'pending':
        case 'processing':
        case 'requires_payment_method':
        case 'requires_confirmation':
          displayStatus = 'Pending';
          break;
        default:
          displayStatus = 'Pending';
      }

      
      const fullTransactionId = transaction.transactionId !== "N/A" && transaction.transactionId 
        ? transaction.transactionId 
        : "N/A";

      // Create short display ID
      const shortTransactionId = fullTransactionId === "N/A" ? "N/A" : fullTransactionId.slice(0, 8).toUpperCase();

      return {
        name: transaction.userName || 'N/A',
        id: transaction.transactionId || "N/A",
        fullTransactionId: fullTransactionId,
        transId: shortTransactionId,
        date: new Date(transaction.createdAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        email: transaction.email || '-',
        maxUsers: transaction.maxUsers,
        paymentMethod: transaction.cardBrand !== "N/A" && transaction.last4Digits !== "N/A"
          ? `${transaction.cardBrand} ****${transaction.last4Digits}`
          : transaction.paymentType !== "N/A" 
            ? transaction.paymentType 
            : 'N/A',
        promocode: transaction.promocode !== "N/A" ? transaction.promocode : 'N/A',
        discount: transaction.discount !== "N/A" ? transaction.discount : 'N/A',
        // Check if amount needs to be converted from cents to dollars
        // If the API returns amounts in cents, divide by 100, otherwise use as-is
        // amountPaid: transaction.amount === 0 ? 'Free' : `$${transaction.amount.toFixed(2)}`,
        amountPaid: transaction.amount === 0 ? 'Free' : `$${transaction.amount}`,
        status: displayStatus
      };
    });
  };

  // Function to fetch payment history from API with search and filter
  const fetchPaymentHistory = async (page: number = 1, limit: number = 10, status: string = 'all', search: string = '') => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Map frontend status filter to API status values
      if (status !== 'all') {
        let apiStatus: string;
        switch (status) {
          case 'success':
            apiStatus = 'succeeded';
            break;
          case 'failed':
            apiStatus = 'failed';
            break;
          case 'pending':
            // You may need to adjust these based on your API's actual pending status values
            apiStatus = 'pending';
            break;
          default:
            apiStatus = status;
        }
        queryParams.append('status', apiStatus);
      }

      // Add search parameter if provided
      if (search.trim()) {
        queryParams.append('search', search.trim());
      }


      const token = getAuthToken();
      
      // Check if token exists
      if (!token) {
        toast.error("Authentication token not found. Please log in again.", {
          position: "top-right"
        });
        setLoading(false);
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Ensure Bearer token is always included
      };


      const response = await fetch(`${ADMIN_GET_ALL_PAYMENT_HISTORY}?${queryParams}`, {
        method: 'GET',
        headers
      });


      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Authentication failed. Please log in again.", {
            position: "top-right"
          });
          
          return;
        } else if (response.status === 404) {
          toast.error("API endpoint not found. Please check the API URL configuration.", {
            position: "top-right"
          });
          return;
        } else if (response.status === 403) {
          toast.error("Access forbidden. You don't have permission to access this resource.", {
            position: "top-right"
          });
          return;
        }
        throw new Error(`Failed to fetch payment history: ${response.status} ${response.statusText}`);
      }

      const result: PaymentApiResponse = await response.json();
      
      if (result.success) {
        const transformedPayments = transformPaymentData(result.data.transactions);
        setPayments(transformedPayments);
        setTotalItems(result.data.pagination.totalItems);
        setTotalPages(result.data.pagination.totalPages);
        
      } else {
        toast.error(result.message || 'Failed to load payment history', {
          position: "top-right"
        });
        setPayments([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      toast.error("Failed to load payment history. Please try again.", {
        position: "top-right"
      });
      setPayments([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Function to generate pagination items with ellipsis
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to maxVisiblePages
      for (let number = 1; number <= totalPages; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => setCurrentPage(number)}
          >
            {number}
          </Pagination.Item>
        );
      }
    } else {
      // Always show first page
      items.push(
        <Pagination.Item
          key={1}
          active={1 === currentPage}
          onClick={() => setCurrentPage(1)}
        >
          1
        </Pagination.Item>
      );

      // Show ellipsis if current page is far from start
      if (currentPage > 3) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let number = startPage; number <= endPage; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => setCurrentPage(number)}
          >
            {number}
          </Pagination.Item>
        );
      }

      // Show ellipsis if current page is far from end
      if (currentPage < totalPages - 2) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      // Always show last page
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === currentPage}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  // Initial load
  useEffect(() => {
    fetchPaymentHistory(1, itemsPerPage, statusFilter, searchTerm);
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPaymentHistory(1, itemsPerPage, statusFilter, searchTerm);
      setCurrentPage(1);
    }, 500); // 500ms delay for debounced search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Effect for pagination and status filter changes
  useEffect(() => {
    fetchPaymentHistory(currentPage, itemsPerPage, statusFilter, searchTerm);
  }, [currentPage, statusFilter]);

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // The actual search will be triggered by the debounced useEffect
  };

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Container fluid className="p-0 payments-information mb-5">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <Card className="filter-block p-1 mb-3 rounded shadow-sm border-0">
        <CardBody>
          <Row className="justify-content-between">
            <Col lg={4} md={12} className="mb-3 mb-lg-0 filters">
              <InputGroup>
                <Form.Control
                  placeholder="Search by email, transaction ID..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {/* <Button variant="outline-secondary" className="border-0">
                  <BiSearch />
                </Button> */}
              </InputGroup>
            </Col>
            <Col lg={8} md={12} className="filters">
              <div className="d-flex justify-content-end search-create">
                <div className="d-flex align-items-center me-0 me-md-3">
                  <span className="me-2" style={{ minWidth: '60px' }}>Filter by</span>
                  <Form.Select
                    style={{ width: 'auto', minWidth: '230px' }}
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {loading ? (
        <Loader text="Loading payments..." />
      ) : (
        <>
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr style={{ whiteSpace: "nowrap" }}>
                  <th>Trans ID</th>
                  <th>Date & Time</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Max Users</th>
                  <th>Payment Type</th>
                  <th>Promocode</th>
                  <th>Discount</th>
                  <th>Amount Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <tr key={payment.id} style={{ whiteSpace: "nowrap" }}>
                      <td className="gilroy-semibold text-info">
                        <div className="d-flex align-items-center">
                          <span className="me-2">{payment.transId}</span>
                          {payment.fullTransactionId !== "N/A" && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  Copy full transaction ID: {payment.fullTransactionId}
                                </Tooltip>
                              }
                            >
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="p-1 border-0"
                                onClick={() => copyToClipboard(payment.fullTransactionId)}
                              >
                                <BiCopy size={14} />
                              </Button>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                      <td>{payment.date}</td>
                      <td>
                        {payment.email.length > 20 ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {payment.email}
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }}>
                              {payment.email.substring(0, 20)}...
                            </span>
                          </OverlayTrigger>
                        ) : (
                          payment.email
                        )}
                      </td>
                      <td>
                        {payment.name.length > 20 ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {payment.name}
                              </Tooltip>
                            }
                          >
                            <span style={{ cursor: 'pointer' }}>
                              {payment.name.substring(0, 20)}...
                            </span>
                          </OverlayTrigger>
                        ) : (
                          payment.name
                        )}
                      </td>
                      <td>{payment.maxUsers}</td>
                      <td>{payment.paymentMethod}</td>
                      <td>{payment.promocode}</td>
                      <td>{payment.discount}</td>
                      <td>{payment.amountPaid}</td>
                      <td>
                        <Badge bg={getBadgeVariant(payment.status)} className="px-3 py-2">
                          {payment.status}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      No payment information found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2 pagination-block">
            <div>
              Showing: {payments.length > 0 ? indexOfFirstItem : 0} to {indexOfLastItem} of {totalItems}
            </div>
            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
                {generatePaginationItems()}
                <Pagination.Next onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default PaymentsInformation;